// Sprint 2026-Jun — VoltLedger integration Phase D
// Retentions (Receivable) — holds per head, FIFO releases, release requests.
//
// Path: /receivable-retentions   (module key: receivable_retentions)
// Rules (client-approved): releases NEVER create Payment Receipt rows (D4);
// historical sales_invoices are never modified (D3); every action is
// history-tracked and releases are audit-logged.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Card } from "@/components/ui/card";
import { useOpenInvoiceDetail } from "@/pages/InvoiceReceivableDetail";
import {
  Check, ChevronLeft, ChevronRight, Clock, Download, ExternalLink, History, Plus,
  RotateCw, Search, Trash2, Unlock, X,
} from "lucide-react";

const inr = (v) => (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const selCls = "border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const LIMIT = 25;

const STATUS_TONE = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Approved: "bg-sky-50 text-sky-700 border-sky-200",
  Rejected: "bg-rose-50 text-rose-700 border-rose-200",
  Settled: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const Badge = ({ s }) => (
  <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_TONE[s] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{s}</span>
);

const QUEUE_TYPES = [
  ["yet_to_raise", "Yet to Raise Retention Request", "bg-slate-50 text-slate-600 border-slate-300"],
  ["request_raised", "Retention Request Raised", "bg-sky-50 text-sky-800 border-sky-200"],
  ["paid_on_request", "Retention Paid On Request", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  ["paid_without_request", "Retention Paid Without Request", "bg-rose-50 text-rose-700 border-rose-200"],
];
const QUEUE_TYPE_TONE = Object.fromEntries(QUEUE_TYPES.map(([k, l, t]) => [k, { label: l, tone: t }]));

const TypeBadges = ({ types }) => (
  <div className="flex flex-col gap-1">
    {(types || []).map((t, i) => {
      const cfg = QUEUE_TYPE_TONE[t.type] || { label: t.type, tone: "bg-slate-50 text-slate-600 border-slate-200" };
      const heads = (t.heads || []).map((h) => h.replace(" Retention", "")).join(", ");
      return (
        <span key={i} data-testid={`type-badge-${t.type}`}
          className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap ${cfg.tone}`}>
          {cfg.label}{heads ? ` — ${heads}` : ""}
        </span>
      );
    })}
  </div>
);

export default function ReceivableRetentions() {
  const [tab, setTab] = useState("queue");
  const [heads, setHeads] = useState([]);
  const [filters, setFilters] = useState({ head: "", status: "", q: "", rtype: "", client_id: "", project_id: "", branch_id: "" });
  const [pickers, setPickers] = useState({ clients: [], projects: [], branches: [] });
  const [exporting, setExporting] = useState(false);

  async function exportExcel() {
    setExporting(true);
    try {
      const f = filters;
      const params = {
        tab,
        ...(f.head && { head: f.head }), ...(f.q && { q: f.q }),
        ...(f.client_id && { client_id: f.client_id }),
        ...(f.project_id && { project_id: f.project_id }),
        ...(f.branch_id && { branch_id: f.branch_id }),
        ...(tab === "queue" ? (f.rtype ? { rtype: f.rtype } : {}) : (f.status ? { status: f.status } : {})),
      };
      const r = await api.get("/retentions/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `retentions_${tab}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  }
  const [queue, setQueue] = useState({ rows: [], total: 0, totals: {} });
  const [holds, setHolds] = useState({ rows: [], total: 0, totals: {} });
  const [reqs, setReqs] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // {type:"add"|"release"|"request"|"history", ...}

  useEffect(() => {
    api.get("/receivables/meta").then((r) => setHeads(r.data.retention_heads || [])).catch(() => {});
    Promise.all([
      api.get("/clients/picker"),
      api.get("/projects-picker"),
      api.get("/branches-picker"),
    ]).then(([c, p, b]) => setPickers({
      clients: (c.data || []).slice().sort((x, y) => (x.name || "").localeCompare(y.name || "")),
      projects: (p.data || []).slice().sort((x, y) => (x.project_name || "").localeCompare(y.project_name || "")),
      branches: b.data || [],
    })).catch(() => {});
  }, []);

  async function load(p = page, f = filters, t = tab) {
    setLoading(true);
    try {
      const params = {
        skip: p * LIMIT, limit: LIMIT,
        ...(f.head && { head: f.head }), ...(f.status && { status: f.status }), ...(f.q && { q: f.q }),
        ...(f.client_id && { client_id: f.client_id }),
        ...(f.project_id && { project_id: f.project_id }),
        ...(f.branch_id && { branch_id: f.branch_id }),
      };
      if (t === "queue") {
        delete params.status;
        if (f.rtype) params.rtype = f.rtype;
        const r = await api.get("/retentions/queue", { params });
        setQueue(r.data);
      } else if (t === "holds") {
        const r = await api.get("/retentions", { params });
        setHolds(r.data);
      } else {
        const r = await api.get("/retention-requests", { params });
        setReqs(r.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load retentions");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(0, filters, tab); setPage(0); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = useOpenInvoiceDetail("/receivable-retentions");

  const totals = tab === "queue" ? (queue.totals || {}) : (holds.totals || {});
  const list = tab === "queue" ? queue : tab === "holds" ? holds : reqs;
  const pages = Math.max(1, Math.ceil((list.total || 0) / LIMIT));

  async function decide(rid, action) {
    try {
      await api.put(`/retention-requests/${rid}/${action}`, {});
      toast.success(`Request ${action === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  }

  async function removeRow(kind, id) {
    if (!window.confirm("Delete this record? This cannot be undone.")) return;
    try {
      await api.delete(kind === "holds" ? `/retentions/${id}` : `/retention-requests/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }

  return (
    <div data-testid="receivable-retentions-page">
      <PageHeader
        eyebrow="Receivables"
        title="Retentions (Receivable)"
        subtitle="Client-held retention per invoice and head — Retention PO Terms, IR, Safety, BG, GST, Other. Releases apply FIFO to the oldest holds and auto-settle approved release requests. Releases never create Payment Receipt entries."
        actions={
          <button data-testid="btn-add-retention" onClick={() => setDialog({ type: "add" })}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> Add Retention
          </button>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {[["Total Held", totals.held, "border-slate-300 bg-slate-100 text-slate-900", "kpi-held"],
          ["Pending (with client)", totals.pending, "border-amber-200 bg-amber-50 text-amber-900", "kpi-pending"],
          ["Released", totals.released, "border-emerald-200 bg-emerald-50 text-emerald-900", "kpi-released"],
          ["Open Requests", totals.open_requests, "border-sky-200 bg-sky-50 text-sky-900", "kpi-open-requests"]].map(([l, v, tone, tid]) => (
          <div key={l} className={`border px-3 py-2.5 ${tone}`} data-testid={tid}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{l}</div>
            <div className="text-sm font-bold mt-0.5">{l === "Open Requests" ? (v || 0) : `₹${inr(v)}`}</div>
          </div>
        ))}
      </div>

      {/* Tabs + filters */}
      <Card>
        <div className="px-4 pt-3 flex items-center justify-between flex-wrap gap-3 border-b border-slate-100">
          <div className="flex gap-1">
            {[["queue", "Retention Queue"], ["holds", "Retention Holds"], ["requests", "Release Requests"]].map(([k, l]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                className={`px-4 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 ${tab === k ? "border-orange-500 text-orange-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex gap-2 pb-2 items-center flex-wrap">
            <select data-testid="filter-client" value={filters.client_id} onChange={(e) => setFilters((f) => ({ ...f, client_id: e.target.value }))} className={selCls + " w-44"}>
              <option value="">All Clients</option>
              {pickers.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="w-56" data-testid="filter-project">
              <SearchableSelect
                testid="filter-project-select"
                value={filters.project_id}
                onChange={(id) => setFilters((f) => ({ ...f, project_id: id || "" }))}
                items={pickers.projects.filter((p) => {
                  if (!filters.client_id) return true;
                  const cl = pickers.clients.find((c) => c.id === filters.client_id);
                  return !p.client_name || !cl || p.client_name === cl.name;
                })}
                getLabel={(p) => p.project_name || ""}
                getSubLabel={(p) => [p.client_name, p.branch_name].filter(Boolean).join(" · ")}
                getSearchText={(p) => `${p.project_name || ""} ${p.project_code || ""} ${p.client_name || ""}`}
                placeholder="All Projects"
                searchPlaceholder="Search project, code or client…"
              />
            </div>
            <select data-testid="filter-branch" value={filters.branch_id} onChange={(e) => setFilters((f) => ({ ...f, branch_id: e.target.value }))} className={selCls + " w-40"}>
              <option value="">All Branches</option>
              {pickers.branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name || b.branch_code || b.id}</option>)}
            </select>
            <select data-testid="filter-head" value={filters.head} onChange={(e) => setFilters((f) => ({ ...f, head: e.target.value }))} className={selCls + " w-40"}>
              <option value="">All Heads</option>
              {heads.map((h) => <option key={h}>{h}</option>)}
            </select>
            {tab === "queue" && (
              <select data-testid="filter-rtype" value={filters.rtype} onChange={(e) => setFilters((f) => ({ ...f, rtype: e.target.value }))} className={selCls + " w-52"}>
                <option value="">All Types</option>
                {QUEUE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            )}
            {tab !== "queue" && (
              <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={selCls + " w-32"}>
                <option value="">All Status</option>
                {(tab === "holds" ? ["Pending", "Paid"] : ["Pending", "Approved", "Rejected", "Settled"]).map((s) => <option key={s}>{s}</option>)}
              </select>
            )}
            <div className="relative w-56 shrink-0">
              <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
              <input data-testid="filter-q" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="Search invoice no…" className={inputCls + " pl-6"} />
            </div>
            <button data-testid="btn-apply-filters" onClick={() => { setPage(0); load(0); }}
              className="bg-slate-900 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-slate-800">
              <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
            </button>
            <button data-testid="btn-export-excel" onClick={exportExcel} disabled={exporting}
              className="border border-emerald-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
              <Download size={12} className={exporting ? "animate-bounce" : ""} /> {exporting ? "Exporting…" : "Export Excel"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {tab === "queue" ? (
            <table className="w-full text-xs" data-testid="retention-queue-table">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Invoice", "Client", "Project", "Branch", "Heads (Pending)", "Type", "Held", "Released", "Pending", "Oldest Hold", "Age", "Open Reqs", ""].map((h) => (
                    <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={13} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
                {!loading && queue.rows.length === 0 && <tr><td colSpan={13} className="px-3 py-8 text-center text-slate-400">No active retention holds — the queue fills automatically when holds with a pending balance exist.</td></tr>}
                {!loading && queue.rows.map((r) => (
                  <tr key={r.invoice_id} className="border-b border-slate-50 hover:bg-orange-50/40 cursor-pointer"
                    data-testid={`queue-row-${r.invoice_id}`}
                    onClick={() => openDetail(r.invoice_id, r.invoice_number)}>
                    <td className="px-2.5 py-2 font-semibold whitespace-nowrap text-sky-700">{r.invoice_number}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.client_name}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.project_name}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.branch_name || "—"}</td>
                    <td className="px-2.5 py-2">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(r.pending_by_head || {}).map(([h, v]) => (
                          <span key={h} className="border border-amber-200 bg-amber-50 text-amber-800 px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap">
                            {h.replace(" Retention", "")}: ₹{inr(v)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-2.5 py-2"><TypeBadges types={r.types} /></td>
                    <td className="px-2.5 py-2 text-right tabular-nums">{inr(r.held)}</td>
                    <td className="px-2.5 py-2 text-right tabular-nums text-emerald-700">{inr(r.released)}</td>
                    <td className="px-2.5 py-2 text-right tabular-nums font-bold">{inr(r.pending)}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.oldest_hold || "—"}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.age_days != null ? `${r.age_days}d` : "—"}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">
                      {r.open_request_count > 0
                        ? <span className="border border-sky-200 bg-sky-50 text-sky-800 px-1.5 py-0.5 text-[10px] font-bold">{r.open_request_count} · ₹{inr(r.open_request_amount)}</span>
                        : "—"}
                    </td>
                    <td className="px-2.5 py-2">
                      <button title="Open Invoice Detail" data-testid={`queue-open-${r.invoice_id}`}
                        onClick={(e) => { e.stopPropagation(); openDetail(r.invoice_id, r.invoice_number); }}
                        className="p-1.5 border border-slate-300 text-slate-500 hover:bg-slate-50"><ExternalLink size={12} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === "holds" ? (
            <table className="w-full text-xs" data-testid="retentions-table">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Invoice", "Client", "Project", "Branch", "Head", "Held", "Released", "Balance", "Status", "Date", "Actions"].map((h) => (
                    <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
                {!loading && holds.rows.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400">No retention holds yet — use “Add Retention”.</td></tr>}
                {!loading && holds.rows.map((r) => {
                  const bal = (r.amount || 0) - (r.paid_amount || 0);
                  return (
                    <tr key={r.id} className="border-b border-slate-50 hover:bg-orange-50/30" data-testid={`retention-row-${r.id}`}>
                      <td className="px-2.5 py-2 font-semibold whitespace-nowrap">
                        <button className="text-sky-700 hover:underline" onClick={() => openDetail(r.invoice_id, r.invoice_number)}>{r.invoice_number}</button>
                      </td>
                      <td className="px-2.5 py-2 whitespace-nowrap">{r.client_name}</td>
                      <td className="px-2.5 py-2 whitespace-nowrap">{r.project_name}</td>
                      <td className="px-2.5 py-2 whitespace-nowrap">{r.branch_name || "—"}</td>
                      <td className="px-2.5 py-2 whitespace-nowrap">{r.head}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums">{inr(r.amount)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums text-emerald-700">{inr(r.paid_amount)}</td>
                      <td className="px-2.5 py-2 text-right tabular-nums font-bold">{inr(bal)}</td>
                      <td className="px-2.5 py-2"><Badge s={r.status} /></td>
                      <td className="px-2.5 py-2 whitespace-nowrap">{r.retention_date}</td>
                      <td className="px-2.5 py-2 whitespace-nowrap">
                        <div className="flex gap-1">
                          {bal > 0 && (
                            <button title="Release (FIFO)" data-testid={`btn-release-${r.id}`}
                              onClick={() => setDialog({ type: "release", row: r })}
                              className="p-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Unlock size={12} /></button>
                          )}
                          {bal > 0 && (
                            <button title="Request Release" data-testid={`btn-request-${r.id}`}
                              onClick={() => setDialog({ type: "request", row: r })}
                              className="p-1.5 border border-sky-300 text-sky-700 hover:bg-sky-50"><Clock size={12} /></button>
                          )}
                          <button title="History" onClick={() => setDialog({ type: "history", row: r })}
                            className="p-1.5 border border-slate-300 text-slate-500 hover:bg-slate-50"><History size={12} /></button>
                          {(r.paid_amount || 0) === 0 && r.status !== "Paid" && (
                            <button title="Delete" data-testid={`btn-delete-${r.id}`} onClick={() => removeRow("holds", r.id)}
                              className="p-1.5 border border-rose-300 text-rose-600 hover:bg-rose-50"><Trash2 size={12} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-xs" data-testid="requests-table">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Invoice", "Client", "Head", "Requested", "Settled", "Status", "Requested By", "Decided By", "Actions"].map((h) => (
                    <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
                {!loading && reqs.rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No release requests.</td></tr>}
                {!loading && reqs.rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`request-row-${r.id}`}>
                    <td className="px-2.5 py-2 font-semibold whitespace-nowrap">
                      <button className="text-sky-700 hover:underline" onClick={() => openDetail(r.invoice_id, r.invoice_number)}>{r.invoice_number}</button>
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.client_name}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.head}</td>
                    <td className="px-2.5 py-2 text-right tabular-nums">{inr(r.amount)}</td>
                    <td className="px-2.5 py-2 text-right tabular-nums text-emerald-700">{inr(r.settled_amount)}</td>
                    <td className="px-2.5 py-2"><Badge s={r.status} /></td>
                    <td className="px-2.5 py-2 whitespace-nowrap text-slate-500">{r.requested_by}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap text-slate-500">{r.decided_by || "—"}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">
                      <div className="flex gap-1">
                        {r.status === "Pending" && (
                          <>
                            <button title="Approve" data-testid={`btn-approve-${r.id}`} onClick={() => decide(r.id, "approve")}
                              className="p-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Check size={12} /></button>
                            <button title="Reject" data-testid={`btn-reject-${r.id}`} onClick={() => decide(r.id, "reject")}
                              className="p-1.5 border border-rose-300 text-rose-600 hover:bg-rose-50"><X size={12} /></button>
                            <button title="Delete" onClick={() => removeRow("requests", r.id)}
                              className="p-1.5 border border-slate-300 text-slate-500 hover:bg-slate-50"><Trash2 size={12} /></button>
                          </>
                        )}
                        <button title="History" onClick={() => setDialog({ type: "history", row: r })}
                          className="p-1.5 border border-slate-300 text-slate-500 hover:bg-slate-50"><History size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between text-xs">
          <div className="text-slate-500">{list.total || 0} record(s) · Page {page + 1} of {pages}</div>
          <div className="flex gap-1.5">
            <button disabled={page === 0} onClick={() => { const p = page - 1; setPage(p); load(p); }}
              className="px-2.5 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={13} /></button>
            <button disabled={page + 1 >= pages} onClick={() => { const p = page + 1; setPage(p); load(p); }}
              className="px-2.5 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronRight size={13} /></button>
          </div>
        </div>
      </Card>

      {dialog?.type === "add" && <AddDialog heads={heads} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
      {dialog?.type === "release" && <ReleaseDialog row={dialog.row} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
      {dialog?.type === "request" && <RequestDialog row={dialog.row} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
      {dialog?.type === "history" && <HistoryDialog row={dialog.row} onClose={() => setDialog(null)} />}
    </div>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-bold text-slate-800">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="mb-3">
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);

function AddDialog({ heads, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [inv, setInv] = useState(null);
  const [form, setForm] = useState({ head: heads[0] || "", amount: "", retention_date: new Date().toISOString().slice(0, 10), remarks: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/retentions/invoice-picker", { params: { q } });
        setOpts(r.data || []);
      } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  async function save() {
    if (!inv) return toast.error("Select an invoice");
    setSaving(true);
    try {
      await api.post(`/sales-invoices/${inv.id}/retentions`, { ...form, amount: Number(form.amount) });
      toast.success("Retention hold created");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create retention"); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Add Retention Hold" onClose={onClose} testid="add-retention-dialog">
      <Field label="Sales Invoice">
        {inv ? (
          <div className="flex items-center justify-between border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs">
            <span className="font-semibold">{inv.invoice_number} · {inv.client_name}</span>
            <button onClick={() => setInv(null)} className="text-slate-400 hover:text-slate-700"><X size={12} /></button>
          </div>
        ) : (
          <>
            <input data-testid="add-invoice-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice number…" className={inputCls} />
            <div className="border border-slate-200 max-h-36 overflow-auto mt-1">
              {opts.map((o) => (
                <button key={o.id} data-testid={`pick-invoice-${o.id}`} onClick={() => setInv(o)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-orange-50 border-b border-slate-50">
                  <span className="font-semibold">{o.invoice_number}</span> · {o.client_name} · {o.project_name} · ₹{inr(o.net_receivable || o.total_amount)}
                </button>
              ))}
              {!opts.length && <div className="px-3 py-2 text-xs text-slate-400">No approved invoices match.</div>}
            </div>
          </>
        )}
      </Field>
      <Field label="Retention Head">
        <select data-testid="add-head" value={form.head} onChange={(e) => setForm((f) => ({ ...f, head: e.target.value }))} className={inputCls}>
          {heads.map((h) => <option key={h}>{h}</option>)}
        </select>
      </Field>
      <Field label="Amount (₹)">
        <input data-testid="add-amount" type="number" min="0" step="0.01" value={form.amount}
          onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={inputCls} />
      </Field>
      <Field label="Retention Date">
        <input type="date" value={form.retention_date} onChange={(e) => setForm((f) => ({ ...f, retention_date: e.target.value }))} className={inputCls} />
      </Field>
      <Field label="Remarks">
        <input value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} className={inputCls} />
      </Field>
      <button data-testid="add-save" disabled={saving} onClick={save}
        className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white py-2.5 text-xs uppercase tracking-wider font-bold">
        {saving ? "Saving…" : "Create Hold"}
      </button>
    </Modal>
  );
}

function ReleaseDialog({ row, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/sales-invoices/${row.invoice_id}/retentions`)
      .then((r) => setPending(r.data.pending_by_head?.[row.head] ?? null))
      .catch(() => {});
  }, [row]);

  async function save() {
    setSaving(true);
    try {
      const r = await api.post("/retentions/release", {
        invoice_id: row.invoice_id, head: row.head, amount: Number(amount),
        release_date: date, remarks,
      });
      toast.success(`Released ₹${inr(Number(amount))} — pending now ₹${inr(r.data.pending_after)}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={`Release Retention — ${row.invoice_number} · ${row.head}`} onClose={onClose} testid="release-dialog">
      <div className="mb-3 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Pending for this head: <b>₹{pending === null ? "…" : inr(pending)}</b>. Releases apply FIFO to the oldest holds and auto-settle approved requests. <b>No Payment Receipt entry is created.</b>
      </div>
      <Field label="Release Amount (₹)">
        <input data-testid="release-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Release Date">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Remarks">
        <input value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
      </Field>
      <button data-testid="release-save" disabled={saving} onClick={save}
        className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-2.5 text-xs uppercase tracking-wider font-bold">
        {saving ? "Releasing…" : "Release (FIFO)"}
      </button>
    </Modal>
  );
}

function RequestDialog({ row, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.post(`/sales-invoices/${row.invoice_id}/retention-requests`, {
        head: row.head, amount: Number(amount), remarks,
      });
      toast.success("Release request submitted");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={`Request Release — ${row.invoice_number} · ${row.head}`} onClose={onClose} testid="request-dialog">
      <Field label="Requested Amount (₹)">
        <input data-testid="request-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Remarks">
        <input value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
      </Field>
      <button data-testid="request-save" disabled={saving} onClick={save}
        className="w-full bg-sky-700 hover:bg-sky-800 text-white py-2.5 text-xs uppercase tracking-wider font-bold">
        {saving ? "Submitting…" : "Submit Request"}
      </button>
    </Modal>
  );
}

function HistoryDialog({ row, onClose }) {
  const events = row.history || [];
  return (
    <Modal title={`History — ${row.invoice_number} · ${row.head}`} onClose={onClose} testid="history-dialog">
      <div className="max-h-72 overflow-auto space-y-2">
        {events.length === 0 && <div className="text-xs text-slate-400">No history recorded.</div>}
        {[...events].reverse().map((e, i) => (
          <div key={i} className="border border-slate-200 px-3 py-2 text-xs">
            <div className="flex justify-between">
              <span className="font-bold uppercase tracking-wider text-[10px] text-orange-600">{e.action}</span>
              <span className="text-slate-400">{(e.at || "").replace("T", " ").slice(0, 19)}</span>
            </div>
            <div className="text-slate-600 mt-0.5">by {e.by}{e.applied !== undefined && <> · applied ₹{inr(e.applied)}</>}{e.amount !== undefined && <> · ₹{inr(e.amount)}</>}</div>
            {e.note && <div className="text-slate-500 italic mt-0.5">“{e.note}”</div>}
          </div>
        ))}
      </div>
    </Modal>
  );
}
